import { AppLayout } from '@components'
import { BackArrow, NextArrow, CloseIcon } from '@svgs'
import type { NextPage, GetStaticProps, GetStaticPaths } from 'next'
import { getGraphQLData } from 'database/graphQL/apollo'
import { queryTeam } from '@queries'
import { ITeamMember, ITeam, ITeamMemberAssociatedEntity } from '@types'
import Link from 'next/link'
import { StructuredText } from 'react-datocms'
import { getImageUrl } from 'utils/getImageUrl'
import Styles from './Bio.module.css'
import { NextSeo } from 'next-seo'
export const getStaticProps: GetStaticProps = async context => {
  const slug = context.params!.slug as string
  let membersArray: ITeamMember[] = []
  const response = await getGraphQLData<{ allTeams: ITeam[] }>(queryTeam)
  response.allTeams.forEach((team: ITeam) => {
    if (team.members) membersArray = membersArray.concat(team.members)
  })
  const currentBioIndex = membersArray.findIndex(item => item.slug === slug)
  if (currentBioIndex < 0) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      teamData: response.allTeams,
      currentBio: membersArray[currentBioIndex],
      nextBio:
        currentBioIndex === membersArray.length - 1
          ? membersArray[0]
          : membersArray[currentBioIndex + 1],
      prevBio:
        currentBioIndex === 0
          ? membersArray[membersArray.length - 1]
          : membersArray[currentBioIndex - 1],
    },
    revalidate: 10,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  let membersArray: ITeamMember[] = []
  const response = await getGraphQLData<{ allTeams: ITeam[] }>(queryTeam)
  response.allTeams.forEach((team: ITeam) => {
    if (team.members) membersArray = membersArray.concat(team.members)
  })

  const paths = membersArray?.map(member => {
    return { params: { slug: member.slug } }
  })
  return {
    paths,
    fallback: true,
  }
}
const TeamDetail: NextPage<{
  currentBio: ITeamMember
  nextBio: ITeamMember
  prevBio: ITeamMember
}> = ({ currentBio, nextBio, prevBio }) => {
  return (
    currentBio && (
      <AppLayout>
        <NextSeo
          title={currentBio?.firstName + ' ' + currentBio?.lastName}
          description={`${currentBio?.title} at MarketX Ventures`}
          canonical={`https://www.marketxventures.com/team/${prevBio.slug}`}
          openGraph={{
            title: `${
              currentBio?.firstName + ' ' + currentBio?.lastName
            } | MarketX Ventures`,
            url: `https://www.marketxventures.com/team/${prevBio.slug}`,
          }}
        />
        <div className="container mx-auto mt-20 min-h-screen px-5 py-5 lg:mt-0 lg:py-10 xl:px-56">
          <div className="flex justify-end">
            <Link href={`/team/${prevBio.slug}`}>
              <div className="group flex h-9 w-9 items-center justify-center rounded border-2 border-[#222222] bg-white hover:border-primary hover:bg-primary 2xl:h-10 2xl:w-[45px]">
                <div className="text-[#222222] group-hover:text-white">
                  <BackArrow />
                </div>
              </div>
            </Link>
            <Link href={`/team/${nextBio?.slug}`}>
              <div className="group ml-2.5 flex h-9 w-9 items-center justify-center rounded border-2 border-[#222222] bg-white hover:border-primary hover:bg-primary 2xl:h-10 2xl:w-[45px]">
                <div className="text-[#222222] group-hover:text-white">
                  <NextArrow />
                </div>
              </div>
            </Link>
            <Link href="/team">
              <div className="group ml-5 flex h-9 w-9 items-center justify-center rounded border-2 border-[#222222] bg-white hover:border-primary hover:bg-primary 2xl:h-10 2xl:w-[45px]">
                <div className="text-[#222222] group-hover:text-white">
                  <CloseIcon />
                </div>
              </div>
            </Link>
          </div>
          <div className="flex flex-col justify-between pt-10 md:flex-row">
            <div className="w-full md:w-[30%]">
              <div
                style={{
                  backgroundImage: `url(${currentBio.photo?.url || ''})`,
                }}
                className="h-[404px] w-full rounded-md bg-contain bg-top bg-no-repeat lg:bg-top"
              />
              <div className="-mt-12 flex flex-col items-center justify-center md:-mt-24">
                <h4 className="pb-2 pt-5 text-center text-lg font-bold text-[#222222] lg:text-xl 2xl:pt-8">
                  {currentBio?.firstName + ' ' + currentBio?.lastName}
                </h4>
                <p className="pb-5 text-center text-sm font-normal text-themeBlack lg:text-lg 2xl:pb-8">
                  {currentBio?.title}
                </p>
                <a
                  href={currentBio?.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="flex">
                  <div className="rounded-md bg-[#2486E0] p-3">
                    <img
                      src={getImageUrl({
                        src: '/images/social/icon-in.png',
                      })}
                      className="h-4 w-4"
                    />
                  </div>
                </a>
              </div>
            </div>
            <div className="mt-8 w-full md:mt-0 md:w-[65%]">
              <div className={`${Styles.bioDetail}`}>
                <StructuredText data={currentBio?.description} />
              </div>
              <div className="mt-12 grid grid-cols-2 items-center justify-items-center gap-y-16 xl:grid-cols-3 xl:gap-y-4">
                {currentBio.associatedEntities.map(
                  (entity: ITeamMemberAssociatedEntity, index: number) => {
                    return (
                      <img
                        key={index}
                        className="w-32"
                        alt={entity.name}
                        src={entity?.logo?.url || ''}
                      />
                    )
                  },
                )}
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    )
  )
}

export default TeamDetail
